import { useLayoutEffect, useRef, useState } from 'react';
import {
  SPLASH_PHASE, SPLASH_GLIDE_TARGET_WAIT_MS, setSplashPhase, isSplashGlideExpected, notifySplashGlideLanded,
  setSplashAwaitingGlideTarget, setSplashVeil, notifySplashGlideNear, SPLASH_GLIDE_NEAR_FRACTION,
} from './splashHandoff';
import {
  findSplashGlideTarget, runSplashGlide, cancelSplashGlide, prefersReducedMotion,
  SPLASH_GLIDE_FADE_MS, SPLASH_GLIDE_FADE_DELAY_MS, SPLASH_GLIDE_PRESETTLED_ATTR,
} from './splashGlide';
import {
  SPLASH_FADE_MS, SPLASH_PLAIN_MARK_OUT_MS, SPLASH_PLAIN_GROUND_DELAY_MS, SPLASH_MARK_IN_DELAY_MS, SPLASH_MARK_IN_MS,
} from './splashTokens';
import { fadeLogoJourney, isLogoJourneyActive } from './logoJourney';
import { createSettleGate, SPLASH_GLIDE_SETTLE_MAX_MS } from './splashGlideSettle';

/** Whatever re-aims happen, 'clear' is published this long after the nominal landing at the latest. */
const LANDING_BACKSTOP_MS = 1000;

/** The glide lands on the target's first child (the wordmark itself). */
function targetRectOf(target) {
  const r = (target.firstElementChild || target).getBoundingClientRect();
  return { left: r.left, top: r.top, width: r.width, height: r.height };
}

/**
 * SplashOverlay's side of the splash → screen handoff (KAN-562).
 *
 * `wantVisible` is the overlay's signal-driven visibility. On every
 * visible → hidden edge it publishes 'leaving' (t0 for the screen below),
 * then:
 *   - a glide target is already on screen (owner + role welcome) → glide now;
 *   - a screen said one is coming (`expectSplashGlide`) → keep
 *     the splash opaque until it mounts, at most SPLASH_GLIDE_TARGET_WAIT_MS,
 *     then glide (or plain fade if it never came);
 *   - otherwise → today's plain fade, untouched.
 * Reduced motion: never a glide — the plain fade, and any target is revealed
 * under the fading splash. 'clear' is published once the fade has finished.
 * A quiet veil (the sign-out cover, no wordmark of its own) never glides: the screen's
 * wordmark is simply there from its first paint, so only the plain fade runs.
 * A re-cover (the splash comes back mid-glide, e.g. a sign-out) or unmount
 * cancels the glide at once: the clone is removed, its landing timer dropped.
 *
 * Returns `holdVisible` (OR it into the overlay's visibility), the fade
 * timing to use, and whether the overlay's own wordmark should hide (the
 * clone has taken its place).
 */
export function useSplashLeaveHandoff(wantVisible, wordmarkRef, veil = false) {
  const [holdVisible, setHoldVisible] = useState(false);
  const [glideFade, setGlideFade] = useState(false);
  const [wordmarkHidden, setWordmarkHidden] = useState(false);
  // Staged wordmark + rail (cold start / silent update): out BEFORE the ground, in AFTER it.
  const [plainStaged, setPlainStaged] = useState(false);
  const [markWaiting, setMarkWaiting] = useState(false);
  const everVisibleRef = useRef(wantVisible);
  const prevRef = useRef(wantVisible);
  const timersRef = useRef([]);
  const rafRef = useRef(null);
  const veilRef = useRef(veil);
  veilRef.current = veil;

  const clearPending = () => {
    setSplashAwaitingGlideTarget(false);
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];
    if (rafRef.current != null && typeof cancelAnimationFrame === 'function') cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
  };

  // Publish the veil with the cover itself (before the screen below mounts), and again if it
  // flips while the cover stays up (e.g. boot -> sign-out cover). Held through the leave.
  useLayoutEffect(() => { if (wantVisible) setSplashVeil(veil); }, [wantVisible, veil]);

  useLayoutEffect(() => {
    const prev = prevRef.current;
    prevRef.current = wantVisible;
    if (wantVisible) {
      clearPending();
      cancelSplashGlide();
      setHoldVisible(false);
      setGlideFade(false);
      setWordmarkHidden(false);
      setPlainStaged(false);
      // A splash coming back over a live screen (silent update reload): the ground goes in first,
      // the wordmark + rail only once it is ~0.8 opaque — never over drawn rows.
      if (!prev && everVisibleRef.current && !prefersReducedMotion()) {
        setMarkWaiting(true);
        timersRef.current.push(setTimeout(() => setMarkWaiting(false), SPLASH_MARK_IN_DELAY_MS));
      }
      everVisibleRef.current = true;
      setSplashPhase(SPLASH_PHASE.COVERED);
      return undefined;
    }
    if (!prev) {
      // Never up (or already left): make sure nobody waits on a phase forever.
      if (!timersRef.current.length) setSplashPhase(SPLASH_PHASE.CLEAR);
      return undefined;
    }

    setMarkWaiting(false);
    setSplashPhase(SPLASH_PHASE.LEAVING);
    const later = (fn, ms) => { timersRef.current.push(setTimeout(fn, ms)); };
    const finishAfter = (ms) => later(() => setSplashPhase(SPLASH_PHASE.CLEAR), ms);

    // No target: the journey element (if the sign-in morph left one resting
    // on the splash) fades out WITH the splash — same length, same curve.
    const plainFade = () => {
      notifySplashGlideNear(); // no glide: the screen's entrance needs no hold
      if (isLogoJourneyActive()) { setWordmarkHidden(true); fadeLogoJourney(SPLASH_FADE_MS); finishAfter(SPLASH_FADE_MS); return; }
      // Cold start, nothing to glide to: the wordmark (and rail) go first, THEN the ground,
      // so the wordmark never sits over drawn content. Total = WORDMARK_OUT + ground fade.
      if (!prefersReducedMotion()) setPlainStaged(true);
      finishAfter(SPLASH_PLAIN_GROUND_DELAY_MS + SPLASH_FADE_MS);
    };
    const launch = (target) => {
      setHoldVisible(false);
      if (!target) { plainFade(); return; }
      // 'clear' = the splash's fade is over AND the logo has landed (a re-aim
      // mid-flight can make the landing later than the leg's nominal length;
      // the target must not be revealed under a still-flying logo).
      let landed = false;
      let faded = false;
      const clearWhenBoth = () => { if (landed && faded) setSplashPhase(SPLASH_PHASE.CLEAR); };
      const ran = runSplashGlide(wordmarkRef.current, target, { onLanded: () => { landed = true; clearWhenBoth(); } });
      if (ran > 0) {
        setGlideFade(true);
        setWordmarkHidden(true);
        later(() => notifySplashGlideNear(), Math.round(ran * SPLASH_GLIDE_NEAR_FRACTION));
        later(() => { faded = true; clearWhenBoth(); }, SPLASH_GLIDE_FADE_DELAY_MS + SPLASH_GLIDE_FADE_MS);
        finishAfter(ran + LANDING_BACKSTOP_MS); // never hangs
      } else {
        plainFade();
      }
    };

    if (veilRef.current) { plainFade(); return undefined; }

    if (prefersReducedMotion()) {
      // No motion: the target is revealed under the fading splash (a crossfade).
      const target = findSplashGlideTarget();
      if (target) notifySplashGlideLanded(target);
      plainFade();
      return undefined;
    }

    if (!findSplashGlideTarget() && !isSplashGlideExpected()) { plainFade(); return undefined; }

    // Keep the splash opaque until the next frame (and, when the target is
    // still mounting in response to 'leaving', a few more — bounded). The
    // launch then commits the glide fade timing together with opacity 0, so
    // the fade never starts on the plain 220ms transition first.
    // Once the target is there, it must also hold still (createSettleGate):
    // the splash stays opaque until its rect reads the same on consecutive
    // frames, at most SPLASH_GLIDE_SETTLE_MAX_MS — so the flight is aimed at
    // the header's FINAL position, not one the panel is about to leave.
    // Exception: a destination that already settled under the cover
    // (SPLASH_GLIDE_PRESETTLED_ATTR — MFA gate) skips the second wait.
    setHoldVisible(true);
    setSplashAwaitingGlideTarget(true);
    const started = Date.now();
    const settled = createSettleGate();
    const poll = () => {
      rafRef.current = null;
      const t = findSplashGlideTarget();
      const noFrames = typeof requestAnimationFrame !== 'function';
      const preSettled = Boolean(t?.hasAttribute?.(SPLASH_GLIDE_PRESETTLED_ATTR));
      if (t && (preSettled || noFrames || settled(targetRectOf(t), Date.now()))) {
        clearPending();
        launch(t);
        return;
      }
      if (!t && (noFrames || Date.now() - started >= SPLASH_GLIDE_TARGET_WAIT_MS)) { clearPending(); launch(null); return; }
      rafRef.current = requestAnimationFrame(poll);
    };
    // Backstop if frames stall (hidden tab): the wait is time-bounded, never frame-bounded.
    later(() => { clearPending(); launch(findSplashGlideTarget()); }, SPLASH_GLIDE_TARGET_WAIT_MS + SPLASH_GLIDE_SETTLE_MAX_MS + 50);
    rafRef.current = typeof requestAnimationFrame === 'function' ? requestAnimationFrame(poll) : null;
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wantVisible]);

  useLayoutEffect(() => () => { clearPending(); cancelSplashGlide(); }, []);

  // Plain (no-glide) leave: wordmark + rail fade out first, the ground follows a beat later.
  const fadeTransition = glideFade
    ? `opacity ${SPLASH_GLIDE_FADE_MS}ms ease-out ${SPLASH_GLIDE_FADE_DELAY_MS}ms`
    : `opacity ${SPLASH_FADE_MS}ms ease-out${plainStaged ? ` ${SPLASH_PLAIN_GROUND_DELAY_MS}ms` : ''}`;
  let markStyle;
  if (plainStaged) markStyle = { opacity: 0, transition: `opacity ${SPLASH_PLAIN_MARK_OUT_MS}ms ease-out` };
  else if (markWaiting) markStyle = { opacity: 0, transition: 'none' };
  else if (everVisibleRef.current) markStyle = { opacity: 1, transition: `opacity ${SPLASH_MARK_IN_MS}ms ease-out` };

  return { holdVisible, fadeTransition, wordmarkHidden, markStyle };
}

export default useSplashLeaveHandoff;
